import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, AlertCircle, ArrowRight } from 'lucide-react';
import PhotoUpload from '@/components/shared/PhotoUpload';

export interface EstimateLine {
  categoryId: string;
  measureType: 'qty' | 'lbs';
  value: number;
}

interface Props {
  categories: { id: string; name: string }[];
  estimateLines: EstimateLine[];
  onEstimateLinesChange: (lines: EstimateLine[]) => void;
  photos: string[];
  onPhotosChange: (p: string[]) => void;
  notes: string;
  onNotesChange: (n: string) => void;
  onContinue: () => void;
}

export default function PickupDetailsStep({
  categories, estimateLines, onEstimateLinesChange,
  photos, onPhotosChange, notes, onNotesChange, onContinue,
}: Props) {
  const minPhotos = Math.max(1, estimateLines.filter((e) => e.categoryId).length);
  const photosValid = photos.length >= minPhotos;
  const notesValid = notes.trim().length > 0;
  const canContinue = photosValid && notesValid;

  function addLine() {
    onEstimateLinesChange([...estimateLines, { categoryId: '', measureType: 'qty', value: 0 }]);
  }
  function removeLine(i: number) {
    onEstimateLinesChange(estimateLines.filter((_, idx) => idx !== i));
  }
  function update(i: number, field: keyof EstimateLine, val: string | number) {
    onEstimateLinesChange(estimateLines.map((e, idx) => (idx === i ? { ...e, [field]: val } : e)));
  }

  return (
    <div className="space-y-6">
      {/* Estimates */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Pre-Intake Category Estimates</Label>
            <Button type="button" variant="outline" size="sm" onClick={addLine} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add Line
            </Button>
          </div>
          {estimateLines.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No estimate lines yet. Click "Add Line" to begin.</p>
          ) : (
            estimateLines.map((est, i) => (
              <div key={i} className="flex items-center gap-3">
                <Select value={est.categoryId} onValueChange={(v) => update(i, 'categoryId', v)}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" className="w-24" placeholder={est.measureType === 'qty' ? 'Qty' : 'Lbs'}
                  value={est.value || ''} onChange={(e) => update(i, 'value', parseFloat(e.target.value) || 0)} />
                <Select value={est.measureType} onValueChange={(v) => update(i, 'measureType', v)}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="qty">Qty</SelectItem>
                    <SelectItem value="lbs">Lbs</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <Card className={!photosValid && photos.length > 0 ? 'border-destructive/50' : ''}>
        <CardContent className="p-6">
          <PhotoUpload photos={photos} onPhotosChange={onPhotosChange} label={`Pickup Photos (min ${minPhotos} required)`} />
          {!photosValid && (
            <p className="mt-2 text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {minPhotos - photos.length} more photo{minPhotos - photos.length > 1 ? 's' : ''} needed
            </p>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className={!notesValid && notes.length > 0 ? 'border-destructive/50' : ''}>
        <CardContent className="p-6">
          <Label className="text-xs text-muted-foreground">Pickup Notes <span className="text-destructive">*</span></Label>
          <Textarea value={notes} onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Condition notes, item counts, observations..." rows={3} />
          {!notesValid && <p className="mt-1 text-xs text-destructive">Required</p>}
        </CardContent>
      </Card>

      {/* Continue */}
      <div className="flex items-center gap-3">
        <Button onClick={onContinue} disabled={!canContinue} className="gap-2">
          Continue to Review <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
